import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import cx from 'classnames';
import styles from './BillingTermCard.module.less';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../generic/Button';
import { LabeledTextInput } from '../../generic/Forms';
import LabeledNumberInput from '../../generic/Forms/LabeledNumberInput';
import * as api from '../../../redux/api';
import Text from '../../generic/Text';
import type { FailedFetchResponse } from '../../../helpers/errors';
import { errorMessagesListFromResponse } from '../../../helpers/errors';
import { BILLING_TERM_URL } from '.';
import { useAsyncStatus } from '../../../hooks';

/**
 * Validates and returns a date object from a string
 * @param dateInput {string} possible date input
 * @returns {Date | null} date
 */
const getAndValidateDate = (dateInput: string): Date | null => {
  const date = new Date(dateInput);

  if (Number.isNaN(date.valueOf())) {
    return null;
  }

  return date;
};

type Props = {
  onTermAdd: () => void;
};

const AddBillingTermForm: VoidFunctionComponent<Props> = ({ onTermAdd }: Props) => {
  const { status, onError, onPending, onSuccess } = useAsyncStatus();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [revisionLimit, setRevisionLimit] = useState<number>(0);
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = async () => {
    const submitErrors = [];
    const termStart = getAndValidateDate(startDate);
    const termEnd = getAndValidateDate(endDate);

    // Validate
    if (!termStart) {
      submitErrors.push('Invalid start date');
    }

    if (!termEnd) {
      submitErrors.push('Invalid end date');
    }

    if (revisionLimit <= 0) {
      submitErrors.push('Revision limit must be greater than 0');
    }

    if (termStart && termEnd && termStart > termEnd) {
      submitErrors.push('Start date cannot be after end date');
    }

    setErrors(submitErrors);
    if (submitErrors.length > 0) {
      return;
    }

    onPending();

    const billingTermRequest = {
      termStart,
      termEnd,
      quantity: revisionLimit,
    };

    try {
      await api.post(BILLING_TERM_URL, billingTermRequest);
    } catch (err: unknown) {
      setErrors(errorMessagesListFromResponse(err as FailedFetchResponse));
      onError(err as FailedFetchResponse);
      return;
    }

    onTermAdd();
    setStartDate('');
    setEndDate('');
    setRevisionLimit(0);
    onSuccess();
  };

  return (
    <div className={cx(styles.addTerm)}>
      <Text.H4>Add a new Billing Term</Text.H4>
      {errors && errors.length !== 0 && errors.map((err) => <div className="alert alert-error">{err}</div>)}
      <div className={cx(styles.dateInput)}>
        <LabeledTextInput
          label="Start Date"
          placeholder="Start Date"
          type="date"
          value={startDate}
          onChange={setStartDate}
        />
        <span />
        <LabeledTextInput
          label="End Date"
          placeholder="End Date"
          type="date"
          value={endDate}
          onChange={setEndDate}
        />
      </div>
      <div className={cx(styles.quantityInput)}>
        <LabeledNumberInput
          label="Revision limit"
          value={revisionLimit.toString()}
          min={1}
          onChange={(val) => setRevisionLimit(parseInt(val, 10) || 0)}
        />
        <LoaderButton
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.LARGE}
          title="Add term"
          isLoading={status.isLoading}
          isDisabled={status.isLoading}
          onClick={onSubmit}
        >
          Add Term
        </LoaderButton>
      </div>
    </div>
  );
};

export default AddBillingTermForm;
